import { useTranslation } from 'react-i18next';

import { cn } from '@/shared/utils/cn';

/*
 * R-050 status presentation (DS-02). A tone is a VISUAL group — the business
 * value is always the text beside it, exactly as the backend reports it, and
 * nothing here collapses or renames a domain state (addendum decisions 13–15).
 *
 *   success    done, accepted, good to go                 green
 *   info       moved on, waiting on nobody in particular  blue
 *   progress   under review — somebody is deciding         Focus Violet
 *   warning    advisory: threshold, partial, running late Amber
 *   exception  a HUMAN MUST ACT: overdue, a supplier's    Orange
 *              question waiting on the buyer
 *   critical   refused, failed, suspended                 red
 *   neutral    not started, inactive                      grey
 *   closed     finished without completing                grey, stronger line
 *   completed  finished                                   Plum
 *
 * Each status keeps the meaning its earlier hue carried; three move to R-050's
 * more specific groups: under-review states to `progress`, `overdue` and
 * `query` to `exception` (addendum decision 3), and `completed` to Plum.
 * `issued`, `sent` and `completed` used to fall through to grey.
 */
export const TONE = {
  success: 'bg-success-surface text-success-ink ring-success-line',
  info: 'bg-info-surface text-info-ink ring-info-line',
  progress: 'bg-progress-surface text-progress-ink ring-progress-line',
  warning: 'bg-warning-surface text-warning-ink ring-warning-line',
  exception: 'bg-exception text-ink-on-exception ring-exception',
  critical: 'bg-critical-surface text-critical-ink ring-critical-line',
  neutral: 'bg-sunken text-ink-muted ring-line',
  closed: 'bg-sunken text-ink-muted ring-line-strong',
  completed: 'bg-brand-100 text-brand-600 ring-brand-200',
} as const;

export type StatusTone = keyof typeof TONE;

const TONES: Record<string, string> = {
  active: TONE.success,
  inactive: TONE.neutral,
  archived: TONE.closed,
  invited: TONE.info,
  suspended: TONE.critical,
  scheduled: TONE.warning,
  // ONB-01 / ONB-02 — readiness and import outcomes.
  ready: TONE.success,
  outstanding: TONE.warning,
  validated: TONE.info,
  applied: TONE.success,
  valid: TONE.info,
  invalid: TONE.critical,
  expiring: TONE.warning,
  overdue: TONE.exception,
  expired: TONE.closed,
  revoked: TONE.critical,

  // Request and approval lifecycles.
  draft: TONE.neutral,
  pending: TONE.progress,
  pending_approval: TONE.progress,
  approved: TONE.success,
  returned: TONE.info,
  rejected: TONE.critical,
  cancelled: TONE.closed,
  procurement_review: TONE.progress,
  sourcing_required: TONE.info,
  // OBS-017 — the need's enquiry was awarded and procurement holds it.
  procurement_processing: TONE.progress,
  po_created: TONE.success,
  skipped: TONE.closed,

  // Purchase order lifecycle (PO-01 / PO-04 / PO-11).
  issued: TONE.info,
  sent: TONE.info,
  completed: TONE.completed,

  // Purchase order invoicing (INV-*): buyer review of invoice evidence.
  not_invoiced: TONE.neutral,
  partially_invoiced: TONE.warning,
  invoiced: TONE.success,

  /*
   * Supplier answers and fulfilment (PO-05 - PO-09).
   *
   * Without these the four supplier answers all rendered in the same grey as
   * `inactive`, so a DECLINE looked exactly like an unanswered order on a
   * tracking screen - the one distinction the buyer most needs to see.
   */
  acknowledged: TONE.success,
  query: TONE.exception,
  partially_available: TONE.warning,
  declined: TONE.critical,

  not_dispatched: TONE.neutral,
  dispatched: TONE.info,
  partially_received: TONE.warning,
  received: TONE.success,
  closed_short: TONE.closed,

  // Sourcing (SRC-01…SRC-12). `open` and `awarded` are the two a buyer scans
  // for; the silence states stay grey because that is what they mean.
  open: TONE.info,
  awarded: TONE.success,
  no_award: TONE.closed,
  responded: TONE.success,
  late: TONE.warning,
  incomplete: TONE.warning,
  withdrawn: TONE.closed,
  non_responsive: TONE.closed,
};

/**
 * `label` exists because several endpoints already return the human label the
 * server used — passing it through keeps the two from drifting apart. Without
 * one, the local table is consulted; a status with no entry in either would
 * otherwise reach a user as a raw enum value.
 */
export function StatusBadge({
  status,
  label,
  tone,
}: {
  status: string;
  label?: string;
  /** A dimension that reads a shared value differently may name its tone. */
  tone?: StatusTone;
}) {
  const { t } = useTranslation();

  const text =
    label ??
    t(`status.${status}`, {
      defaultValue: t(`enums.request_status.${status}`, {
        defaultValue: t(`enums.approval_status.${status}`, { defaultValue: status }),
      }),
    });

  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium ring-1 ring-inset',
        tone ? TONE[tone] : (TONES[status] ?? TONE.neutral),
      )}
    >
      {text}
    </span>
  );
}
